import generateHeaders from "../utils/generateHeaders";
import api from "./index";

// ============ CONSTANTES ============
const ENDPOINTS = {
  ACCREDITATION_QUOTA: "/accreditation/quota",
  ADMIN_ACCREDITATION: "/admin/accreditation/",
  ACCREDITATION: "/accreditation",
  ASSOCIATE_COLLABORATORS: "/associate-collaborators",
  CREDENTIAL_REPORTS_TOTEM: "/credential-reports/totem",
  CREDENTIAL_SCHEDULE: "/credential-schedule",
  CREDENTIAL_TYPES: "/credential/types",
};

// ============ QUOTA & INFO ============

/**
 * Busca cota de credenciais por oportunidade, evento e usuário
 * Endpoint: GET /accreditation/quota
 * @param {Object} params - Parâmetros da requisição
 * @returns {Promise<Object>} Dados de cota de credenciais
 */
export const getCredentialsQuotaService = async (params) =>
  await api.get(ENDPOINTS.ACCREDITATION_QUOTA, { params });

/**
 * Busca credenciais por aba (associadas, impressas ou retiradas)
 * Endpoint: PATCH /admin/accreditation/
 * @param {Object} data - Dados da requisição
 * @param {string} token - Token de autenticação
 * @returns {Promise<Object>} Dados de credenciais por aba
 */
export const getCredentialsInfoService = async (data, token) => {
  const headers = generateHeaders(token);
  return await api.patch(ENDPOINTS.ADMIN_ACCREDITATION, data, headers);
};

// ============ ACCREDITATION ============

/**
 * Lista todas as credenciais
 * Endpoint: GET /accreditation
 * @param {Object} params - Parâmetros de paginação e filtro
 * @returns {Promise<Object>} Lista de credenciais
 */
export const getListCredentialsService = async (params) =>
  await api.get(ENDPOINTS.ACCREDITATION, { params });

/**
 * Busca uma credencial específica por ID
 * Endpoint: GET /accreditation/:id
 * @param {string|number} id - ID da credencial
 * @returns {Promise<Object>} Dados da credencial
 */
export const getCredentialService = async (id) =>
  await api.get(`${ENDPOINTS.ACCREDITATION}/${id}`);

/**
 * Associa uma nova credencial
 * Endpoint: POST /accreditation
 * @param {Object} data - Dados da credencial
 * @returns {Promise<Object>} Credencial criada
 */
export const handleAssociateCredentialService = async (data) =>
  await api.post(ENDPOINTS.ACCREDITATION, data);

/**
 * Atualiza uma credencial existente
 * Endpoint: PUT /accreditation/:id
 * @param {Object} data - Dados da credencial (inclui id)
 * @returns {Promise<Object>} Credencial atualizada
 */
export const updateAssociateCredentialService = async (data) =>
  await api.put(`${ENDPOINTS.ACCREDITATION}/${data.id}`, data);

/**
 * Remove uma credencial
 * Endpoint: DELETE /accreditation/:id
 * @param {string|number} id - ID da credencial
 * @returns {Promise<Object>} Resposta da deleção
 */
export const removeAssociateCredentialService = async (id) =>
  await api.delete(`${ENDPOINTS.ACCREDITATION}/${id}`);

// ============ ASSOCIATE COLLABORATORS ============

/**
 * Busca colaborador de um contrato
 * Endpoint: GET /associate-collaborators/by-user
 * @param {Object} params - Parâmetros { idContract, idCredential }
 * @returns {Promise<Object>} Dados do colaborador
 */
export const getCollaboratorOfContractService = async (params) =>
  await api.get(
    `${ENDPOINTS.ASSOCIATE_COLLABORATORS}/by-user?opportunity=${params.idContract}&credential=${params.idCredential}`
  );

// ============ CREDENTIAL SCHEDULE ============

/**
 * Busca cronograma de credenciais
 * Endpoint: GET /credential-schedule
 * @param {string|number} event - ID do evento
 * @param {string|number} opportunity - ID da oportunidade
 * @returns {Promise<Object>} Dados do cronograma
 */
export const getCredentialScheduleService = async (event, opportunity) =>
  await api.get(
    `${ENDPOINTS.CREDENTIAL_SCHEDULE}?event_id=${event}&opportunity_id=${opportunity}`
  );

/**
 * Cria novo cronograma de credenciais
 * Endpoint: POST /credential-schedule
 * @param {Object} params - Parâmetros do cronograma
 * @returns {Promise<Object>} Cronograma criado
 */
export const postCredentialScheduleService = async (params) =>
  await api.post(ENDPOINTS.CREDENTIAL_SCHEDULE, { ...params });

/**
 * Atualiza cronograma de credenciais
 * Endpoint: PUT /credential-schedule/:id
 * @param {Object} params - Parâmetros do cronograma
 * @param {string|number} id - ID do cronograma
 * @returns {Promise<Object>} Cronograma atualizado
 */
export const editCredentialScheduleService = async (params, id) =>
  await api.put(`${ENDPOINTS.CREDENTIAL_SCHEDULE}/${id}`, { ...params });

/**
 * Remove cronograma de credenciais
 * Endpoint: DELETE /credential-schedule/:id
 * @param {string|number} id - ID do cronograma
 * @returns {Promise<Object>} Resposta da deleção
 */
export const deleteCredentialScheduleService = async (id) =>
  await api.delete(`${ENDPOINTS.CREDENTIAL_SCHEDULE}/${id}`);

/**
 * Busca locais de retirada disponíveis
 * Endpoint: GET /credential-schedule/withdrawal-places
 * @param {Object} params - Parâmetros de filtro
 * @returns {Promise<Object>} Locais disponíveis
 */
export const getCredentialScheduleAvailablePlacesService = async (params) =>
  await api.get(`${ENDPOINTS.CREDENTIAL_SCHEDULE}/withdrawal-places`, {
    params,
  });

/**
 * Busca datas disponíveis
 * Endpoint: GET /credential-schedule/available-dates
 * @param {Object} params - Parâmetros de filtro
 * @returns {Promise<Object>} Datas disponíveis
 */
export const getCredentialScheduleAvailableDatesService = async (params) =>
  await api.get(`${ENDPOINTS.CREDENTIAL_SCHEDULE}/available-dates`, { params });

/**
 * Busca horários disponíveis
 * Endpoint: GET /credential-schedule/available-times
 * @param {Object} params - Parâmetros de filtro
 * @returns {Promise<Object>} Horários disponíveis
 */
export const getCredentialScheduleAvailableTimesService = async (params) =>
  await api.get(`${ENDPOINTS.CREDENTIAL_SCHEDULE}/available-times`, { params });

// ============ CREDENTIAL REPORTS TOTEM ============

/**
 * Confirma impressão de credencial no totem
 * Endpoint: PATCH /credential-reports/totem/confirm
 * @param {Object} config - { activeToken, credentialsId }
 * @returns {Promise<Object>} Resposta da confirmação
 */
export const confirmCredentialPrintTotem = async ({
  activeToken,
  credentialsId,
}) =>
  api.patch(
    `${ENDPOINTS.CREDENTIAL_REPORTS_TOTEM}/confirm`,
    { credentialsId },
    {
      headers: {
        Authorization: `Bearer ${activeToken}`,
      },
    }
  );

/**
 * Cancela impressão de credencial no totem
 * Endpoint: PATCH /credential-reports/totem/cancel
 * @param {Object} config - { activeToken, credentialsId }
 * @returns {Promise<Object>} Resposta do cancelamento
 */
export const confirmCredentialPrintCancelTotem = async ({
  activeToken,
  credentialsId,
}) =>
  api.patch(
    `${ENDPOINTS.CREDENTIAL_REPORTS_TOTEM}/cancel`,
    { credentialsId },
    {
      headers: {
        Authorization: `Bearer ${activeToken}`,
      },
    }
  );

/**
 * Imprime credencial no totem
 * Endpoint: PATCH /credential-reports/totem
 * @param {Object} config - { activeToken, extension, ...body }
 * @returns {Promise<Object>} Resposta da impressão
 */
export const printCredentialTotem = async ({
  activeToken,
  extension,
  ...body
}) =>
  api.patch(ENDPOINTS.CREDENTIAL_REPORTS_TOTEM, body, {
    params: { extension },
    headers: {
      Authorization: `Bearer ${activeToken}`,
    },
  });

/**
 * Busca todos os templates do totem
 * Endpoint: GET /credential-reports/totem/templates
 * @param {string} token - Token de autenticação
 * @returns {Promise<Object>} Lista de templates
 */
export const getAllTemplatesOfTotem = async (token) => {
  const headers = generateHeaders(token);
  return await api.get(
    `${ENDPOINTS.CREDENTIAL_REPORTS_TOTEM}/templates?extension=png`,
    headers
  );
};

// ============ CREDENTIAL TYPES ============

/**
 * Busca todos os tipos de credenciais
 * Endpoint: GET /credential/types
 * @param {Object} config - { token }
 * @returns {Promise<Object>} Lista de tipos de credenciais
 */
export const getAllCredentialTypes = async ({ token }) => {
  const headers = generateHeaders(token);
  return await api.get(ENDPOINTS.CREDENTIAL_TYPES, headers);
};

// INFORMACP-4386 — reimpressao do protocolo (2a Via) pelo id do protocolo. O backend serve o PDF
// original congelado como "2a Via" (gerada sob demanda e cacheada). Protocolo imutavel; correcao
// de dados gera um novo protocolo.
export const getProtocolPdfService = async (protocolId, token) =>
  await api.get(`/credential-reports/${protocolId}`, generateHeaders(token));

/**
 * Correção e reimpressão de credencial a partir da aba de Retiradas (INFORMACP-4388).
 * Contratos descritos no manual de integração do backend.
 */

// Pré-preenche o formulário com os valores ATUAIS da fonte.
export const getCredentialEditForm = async ({
  token,
  credentialId,
  eventId,
  opportunityId,
}) =>
  api.get(`/credential-reports/credential/${credentialId}/edit-form`, {
    ...generateHeaders(token),
    params: { eventId, opportunityId },
  });

// Gera o PDF, grava snapshot + auditoria e, opcionalmente, promove nome/cargo à fonte.
export const postCredentialEditReprint = async ({
  token,
  credentialId,
  body,
}) =>
  api.post(
    `/credential-reports/credential/${credentialId}/edit-reprint`,
    body,
    generateHeaders(token)
  );

// Histórico paginado das correções/reimpressões (auditoria de/para).
export const getCredentialEditHistory = async ({
  token,
  credentialId,
  eventId,
  opportunityId,
  page = 0,
  size = 20,
}) =>
  api.get(`/credential-reports/credential/${credentialId}/edit-history`, {
    ...generateHeaders(token),
    params: { eventId, opportunityId, page, size },
  });
